import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Mail, Phone, Download, Droplets, Settings, Shield, Filter, Waves, Fish } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

const SelfCleaningSuctionScreensPage = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "What clearances and placement considerations are critical for SCS2 installation?",
      answer: "SCS2 screens require adequate submergence depth (minimum 3-5 feet depending on model) and clearance from bottom/walls to prevent debris recirculation. Maintain 2-3 screen diameters distance from canal bottom to avoid stirring sediment. Position perpendicular to flow when possible to minimize impingement. Avoid placing near sharp bends or turbulent zones. Allow clearance for the internal spray bar to rotate (the screen itself does not rotate; there are no external moving parts). Always verify local fish screen regulations for required approach velocities and clearances."
    },
    {
      question: "What types of debris can SCS2 screens handle?",
      answer: "SCS2 screens excel with organic debris including leaves, grass clippings, algae, small twigs, moss, and aquatic vegetation. The internal spray bar effectively removes sticky/fibrous materials as it rotates inside the stationary screen cylinder. Can handle moderate sand/silt but heavy sediment loads may require more frequent cleaning cycles. Not recommended for heavy trash, plastic bags, or large woody debris—consider trash rack upstream. The self-cleaning action prevents screen blinding from most agricultural and natural organic matter. Proven reliable in manure ponds and dairy lagoons with high-solids, fibrous slurry environments."
    },
    {
      question: "What flow rate and pressure does the spray bar require?",
      answer: "Spray bar flow requirements range from 5-20 GPM depending on screen size (see flow ladder table). Spray bar pressure should be 40-60 PSI for effective cleaning. This can be supplied by a small booster pump, clean water header, or tapped off the discharge side of the main pump (if adequate pressure available). Total spray bar water usage is minimal—typically 0.5-2% of intake flow during cleaning cycles. Many installations use a small dedicated spray pump with pressure switch and timer control."
    },
    {
      question: "How do I prepare SCS2 screens for winter shutdown or freezing conditions?",
      answer: "For seasonal shutdown: (1) Remove screen from water and drain completely. (2) Clean and inspect bearings, seals, and spray nozzles. (3) Lubricate bearings and moving parts. (4) Store indoors if possible, or cover thoroughly. (5) Disconnect spray bar and drain all lines. For installations operating through winter in freezing climates, consider heated spray bar lines, insulated bearings, or switch to passive PSF screens. Running water (continuous slow rotation) can prevent freeze-up in moderate cold, but severe conditions may require heated components or seasonal removal."
    },
    {
      question: "Can SCS2 screens meet fish protection and compliance requirements?",
      answer: "Yes, when properly sized and installed. SCS2 screens typically use 3/32\" or 1/8\" perforations or equivalent mesh that meets most fish exclusion requirements. The sweeping flow pattern prevents impingement—fish are swept away rather than pinned to screen. Approach velocity through screen openings is designed to stay below regulatory limits (typically 0.2-0.5 fps depending on jurisdiction). Always consult local fish and game regulations. CTC filtration specialists can provide compliance documentation and approach velocity calculations for permit applications."
    },
    {
      question: "How often do SCS2 screens require maintenance?",
      answer: "Routine inspection: monthly during operating season. Check for debris accumulation, bearing noise, spray bar function, and screen damage. Annual maintenance: disassemble, clean, inspect bearings and seals, replace worn components. Spray bar nozzles should be cleaned quarterly or when clogging suspected. Bearing life: 3-5 years in typical applications, longer with sealed bearings option. Screen mesh/perforations: inspect annually, replace if damaged—typically 5-10 year life. Heavy debris loading may require more frequent bearing service."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="SCS2 Self-Cleaning Suction Screens | Intake Protection | Fish Compliant | CTC Waterworks"
        description="SCS2 automated self-cleaning intake screens for rivers, canals, ponds, sumps, and manure lagoons. Internal spray bar rotates inside stationary screen—no external moving parts. Fish-friendly design, continuous intake protection. Complete flow ladder, options, and specifications."
        canonical="https://www.ctcwaterworks.com/filtration/suction-screens/scs2"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/suction-screens" className="text-gray-600 hover:text-ctc-blue">Suction Screens</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">SCS2</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-8 pb-6 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Main Content - Left */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-4">
                  <Waves className="text-ctc-blue mr-4" size={48} />
                  <div>
                    <h1 className="text-5xl font-bold text-gray-900">
                      SCS2 Self-Cleaning Suction Screens
                    </h1>
                  </div>
                </div>
                <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
                  Available with configurations meeting NOAA/WDFW fish-screen approach-velocity and open-area criteria — approved for surface-water and dam intake use.
                </p>

                <div className="mb-8 flex justify-center">
                  <img src="/SCS 4.jpeg" alt="SCS2 Application" className="max-w-full h-auto transform -rotate-90" style={{maxWidth: '400px'}} />
                </div>

                <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-lg p-6 mb-6">
                  <h3 className="font-bold text-lg mb-3 text-gray-900">SCS2 = Self-Cleaning Suction 2nd Generation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The SCS2 features a stationary perforated cylinder with an automated rotating internal spray bar. Water from the pump discharge side is diverted to the spray bar, which rotates inside the screen to eject accumulated debris while maintaining continuous intake flow. The screen does not rotate—there are no external moving parts. Ideal for agricultural, municipal, and industrial intakes in rivers, canals, ponds, sumps, and manure lagoons with organic debris, algae, and moderate to high solids loading. Configurations available meeting NOAA/WDFW fish-screen criteria for hydropower and dam applications.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Rail - CTAs */}
            <div>
              <div className="bg-white rounded-lg shadow-xl p-6 space-y-4 sticky top-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Resources & Support</h3>

                <Link
                  to="/contact?topic=filtration"
                  className="block w-full bg-ctc-blue text-white px-6 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center shadow-lg"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Specialist
                </Link>

                <div className="space-y-2 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">Download Resources</h4>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-ctc-blue p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>SCS2 Sell Sheet (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-ctc-blue p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Dimensional Drawing (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-ctc-blue p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Spec Table (CSV)</span>
                  </a>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3 text-gray-700 mb-3">
                    <Phone size={20} />
                    <span className="text-sm">(509) 901-7476</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Mail size={20} />
                    <a href="mailto:Andre@ctcwaterworks.com" className="text-sm hover:text-ctc-blue underline">
                      Andre@ctcwaterworks.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Why Choose SCS2?</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-6">
              <Shield className="text-ctc-blue mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Continuous Protection</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Automated cleaning maintains intake flow without shutdowns. Internal spray bar rotation prevents blinding—no manual intervention required during operation.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-lg p-6">
              <Droplets className="text-green-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Spray-Bar Cleaning</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                High-pressure spray bar rotates inside stationary screen to eject debris. Simple, proven technology with minimal maintenance requirements and no external moving parts.
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6">
              <Settings className="text-orange-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Rugged Options</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Harsh-environment sealed bearings, deflection cones for debris shedding, multiple mounting configurations for various installations.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-300 rounded-lg p-6">
              <Filter className="text-gray-700 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">System Integration</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Pair with BIG or TIG inline filters for complete two-stage protection. SCS2 handles bulk debris, inline filter provides final polishing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Flow Ladder Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Self-Cleaning Suction Screens — Specifications</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Select SCS2 model based on your pump flow rate. Spray bar requirements are minimal—typically 0.5-2% of intake flow.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex justify-center">
              <img src="/SCS3.jpeg" alt="SCS2 Technical Drawing" className="max-w-full h-auto" />
            </div>
            <div className="flex justify-center">
              <img src="/SCS Diagram.jpeg" alt="SCS2 Function Diagram" className="max-w-full h-auto" />
            </div>
            <div className="flex justify-center">
              <img src="/SCS Diagram3.jpeg" alt="SCS2 Installation Diagram" className="max-w-full h-auto" />
            </div>
          </div>

          <div className="flex justify-center mt-3 mb-3">
            <img src="/SCS Size Diagram.png" alt="SCS2 Size Diagram" className="max-w-full h-auto transform rotate-90" style={{maxWidth: '4800px'}} />
          </div>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-ctc-blue to-blue-700 text-white sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Flow (GPM)</th>
                  <th className="px-4 py-3 text-left font-semibold">Item #</th>
                  <th className="px-4 py-3 text-left font-semibold">Matching Pressure Filter</th>
                  <th className="px-4 py-3 text-left font-semibold">A (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">B (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">C (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">D</th>
                  <th className="px-4 py-3 text-left font-semibold">E</th>
                  <th className="px-4 py-3 text-left font-semibold">Spray Bar GPM</th>
                  <th className="px-4 py-3 text-left font-semibold">Est. Weight (lb)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">100</td>
                  <td className="px-4 py-3 font-semibold">BA217</td>
                  <td className="px-4 py-3">BA509</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">13</td>
                  <td className="px-4 py-3">17</td>
                  <td className="px-4 py-3">¾ NPTM</td>
                  <td className="px-4 py-3">3 NPTM</td>
                  <td className="px-4 py-3">11</td>
                  <td className="px-4 py-3">60</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">200</td>
                  <td className="px-4 py-3 font-semibold">BA218</td>
                  <td className="px-4 py-3">BA509</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">15</td>
                  <td className="px-4 py-3">19</td>
                  <td className="px-4 py-3">¾ NPTM</td>
                  <td className="px-4 py-3">4 NPTM</td>
                  <td className="px-4 py-3">12</td>
                  <td className="px-4 py-3">65</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">300</td>
                  <td className="px-4 py-3 font-semibold">BA219</td>
                  <td className="px-4 py-3">BA509</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">17</td>
                  <td className="px-4 py-3">21</td>
                  <td className="px-4 py-3">¾ NPTM</td>
                  <td className="px-4 py-3">5 FLG</td>
                  <td className="px-4 py-3">15</td>
                  <td className="px-4 py-3">70</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">400</td>
                  <td className="px-4 py-3 font-semibold">BA220</td>
                  <td className="px-4 py-3">BA509</td>
                  <td className="px-4 py-3">16</td>
                  <td className="px-4 py-3">17</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">1 NPTM</td>
                  <td className="px-4 py-3">6 FLG</td>
                  <td className="px-4 py-3">17</td>
                  <td className="px-4 py-3">76</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">600</td>
                  <td className="px-4 py-3 font-semibold">CA221</td>
                  <td className="px-4 py-3">AA559</td>
                  <td className="px-4 py-3">16</td>
                  <td className="px-4 py-3">23</td>
                  <td className="px-4 py-3">31</td>
                  <td className="px-4 py-3">1 NPTM</td>
                  <td className="px-4 py-3">8 FLG</td>
                  <td className="px-4 py-3">26</td>
                  <td className="px-4 py-3">82</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">800</td>
                  <td className="px-4 py-3 font-semibold">CA222</td>
                  <td className="px-4 py-3">AA559</td>
                  <td className="px-4 py-3">16</td>
                  <td className="px-4 py-3">28</td>
                  <td className="px-4 py-3">33</td>
                  <td className="px-4 py-3">1 NPTM</td>
                  <td className="px-4 py-3">10 FLG</td>
                  <td className="px-4 py-3">34</td>
                  <td className="px-4 py-3">90</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">1000</td>
                  <td className="px-4 py-3 font-semibold">CA223</td>
                  <td className="px-4 py-3">AA559</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">20</td>
                  <td className="px-4 py-3">28</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">10 FLG</td>
                  <td className="px-4 py-3">26</td>
                  <td className="px-4 py-3">140</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">1200</td>
                  <td className="px-4 py-3 font-semibold">CA224</td>
                  <td className="px-4 py-3">AA559</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">22</td>
                  <td className="px-4 py-3">30</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">10 FLG</td>
                  <td className="px-4 py-3">29</td>
                  <td className="px-4 py-3">160</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">1400</td>
                  <td className="px-4 py-3 font-semibold">DA225</td>
                  <td className="px-4 py-3">AA559</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">25</td>
                  <td className="px-4 py-3">33</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">12 FLG</td>
                  <td className="px-4 py-3">31</td>
                  <td className="px-4 py-3">180</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">1600</td>
                  <td className="px-4 py-3 font-semibold">DA226</td>
                  <td className="px-4 py-3">AA559</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">28</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">12 FLG</td>
                  <td className="px-4 py-3">34</td>
                  <td className="px-4 py-3">210</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">2000</td>
                  <td className="px-4 py-3 font-semibold">DA227</td>
                  <td className="px-4 py-3">AA560</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">34</td>
                  <td className="px-4 py-3">42</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">14 FLG</td>
                  <td className="px-4 py-3">41</td>
                  <td className="px-4 py-3">230</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">2400</td>
                  <td className="px-4 py-3 font-semibold">DA228</td>
                  <td className="px-4 py-3">CA63</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">40</td>
                  <td className="px-4 py-3">48</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">16 FLG</td>
                  <td className="px-4 py-3">46</td>
                  <td className="px-4 py-3">260</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">3000</td>
                  <td className="px-4 py-3 font-semibold">DA229</td>
                  <td className="px-4 py-3">CA63</td>
                  <td className="px-4 py-3">30</td>
                  <td className="px-4 py-3">40</td>
                  <td className="px-4 py-3">48</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">18 FLG</td>
                  <td className="px-4 py-3">46</td>
                  <td className="px-4 py-3">300</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">3600</td>
                  <td className="px-4 py-3 font-semibold">DA260</td>
                  <td className="px-4 py-3">CA63</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">40</td>
                  <td className="px-4 py-3">48</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">20 FLG</td>
                  <td className="px-4 py-3">46</td>
                  <td className="px-4 py-3">433</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">4000</td>
                  <td className="px-4 py-3 font-semibold">BA563</td>
                  <td className="px-4 py-3">CA64</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">45</td>
                  <td className="px-4 py-3">59</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">20 FLG</td>
                  <td className="px-4 py-3">56</td>
                  <td className="px-4 py-3">465</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">4500</td>
                  <td className="px-4 py-3 font-semibold">BA564</td>
                  <td className="px-4 py-3">CA64</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">54</td>
                  <td className="px-4 py-3">64</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">22 FLG</td>
                  <td className="px-4 py-3">59</td>
                  <td className="px-4 py-3">500</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">5000</td>
                  <td className="px-4 py-3 font-semibold">BA565</td>
                  <td className="px-4 py-3">CA64</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">59</td>
                  <td className="px-4 py-3">69</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">24 FLG</td>
                  <td className="px-4 py-3">64</td>
                  <td className="px-4 py-3">535</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">5500</td>
                  <td className="px-4 py-3 font-semibold">BA566</td>
                  <td className="px-4 py-3">CA64</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">64</td>
                  <td className="px-4 py-3">75</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">24 FLG</td>
                  <td className="px-4 py-3">69</td>
                  <td className="px-4 py-3">570</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">6000</td>
                  <td className="px-4 py-3 font-semibold">BA567</td>
                  <td className="px-4 py-3">CA64</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">69</td>
                  <td className="px-4 py-3">81</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">24 FLG</td>
                  <td className="px-4 py-3">74</td>
                  <td className="px-4 py-3">605</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">6500</td>
                  <td className="px-4 py-3 font-semibold">BA571</td>
                  <td className="px-4 py-3">CA64</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">74</td>
                  <td className="px-4 py-3">86</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">30 FLG</td>
                  <td className="px-4 py-3">87</td>
                  <td className="px-4 py-3">640</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">7000</td>
                  <td className="px-4 py-3 font-semibold">BA572</td>
                  <td className="px-4 py-3">CA64</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">79</td>
                  <td className="px-4 py-3">91</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">30 FLG</td>
                  <td className="px-4 py-3">100</td>
                  <td className="px-4 py-3">675</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">7500</td>
                  <td className="px-4 py-3 font-semibold">BA573</td>
                  <td className="px-4 py-3">CA64</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">84</td>
                  <td className="px-4 py-3">97</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">30 FLG</td>
                  <td className="px-4 py-3">115</td>
                  <td className="px-4 py-3">710</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">8000</td>
                  <td className="px-4 py-3 font-semibold">BA574</td>
                  <td className="px-4 py-3">CA64</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">89</td>
                  <td className="px-4 py-3">102</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">30 FLG</td>
                  <td className="px-4 py-3">130</td>
                  <td className="px-4 py-3">745</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">8500</td>
                  <td className="px-4 py-3 font-semibold">BA575</td>
                  <td className="px-4 py-3">CA64</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">94</td>
                  <td className="px-4 py-3">108</td>
                  <td className="px-4 py-3">2 NPTF</td>
                  <td className="px-4 py-3">36 FLG</td>
                  <td className="px-4 py-3">145</td>
                  <td className="px-4 py-3">780</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Note:</span> Pressure Filter .032 perf. SCS2 no smaller than .062 perf on outside (.093 standard).
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">How SCS2 Self-Cleaning Works</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Waves className="mr-3 text-ctc-blue" size={28} />
                Stationary Screen Cylinder
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Perforated stainless steel cylinder remains stationary—the screen does not rotate. Water flows inward through screen openings while debris accumulates on exterior. The internal spray bar rotates to clean the screen surface continuously, with no external moving parts.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Stationary screen—no external moving parts</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">3/32" or 1/8" perforations (fish-friendly)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">No flow interruption during cleaning</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Proven in manure ponds / dairy lagoons</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Droplets className="mr-3 text-green-600" size={28} />
                Spray-Bar Action
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                High-pressure spray bar positioned inside the stationary cylinder rotates and directs water outward through screen openings. As the spray bar rotates past each section of screen, accumulated debris is ejected. Spray activates on timer or differential pressure.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">40-60 PSI spray pressure recommended</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">5-20 GPM depending on screen size</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Timer or ΔP controlled activation</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-r-lg">
            <div className="flex items-start">
              <Fish className="text-green-600 mr-4 flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Fish-Compliant Intake Protection</h4>
                <p className="text-gray-700 leading-relaxed">
                  SCS2 screens meet most fish protection requirements when properly sized. The sweeping flow across the stationary screen cylinder prevents fish impingement—fish are swept away rather than pinned. Approach velocity through screen openings is calculated to stay below regulatory limits (typically 0.2-0.5 fps). Available with 3/32" or 1/8" perforations meeting fish exclusion standards. Our filtration specialists can provide compliance documentation and approach velocity calculations for permit applications.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">SCS2 Applications</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Proven across diverse water sources and challenging intake conditions
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Surface Water Intakes</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Rivers and canals with organic debris, leaves, algae</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Agricultural ponds and sumps</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Dam penstock intakes (fish-compliant configurations)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Hydropower facilities requiring continuous operation</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border-2 border-green-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">High-Solids Environments</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700"><span className="font-semibold">Manure ponds and dairy lagoons</span> — proven reliable in high-solids, fibrous slurry for effluent irrigation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Municipal wastewater and reclaimed water systems</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Industrial process water with variable debris loads</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Aquaculture and fish farm intakes</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-600 p-6 rounded-r-lg max-w-4xl mx-auto">
            <h4 className="font-bold text-gray-900 mb-2">Dairy & Livestock Operations</h4>
            <p className="text-gray-700 leading-relaxed">
              SCS2 screens excel in manure lagoon and dairy effluent applications where fibrous solids, high organic loads, and continuous operation are critical. The internal spray bar effectively removes sticky, fibrous materials without clogging, making it ideal for lagoon pumping systems and effluent irrigation networks. No external moving parts means reliable performance even in harsh slurry environments.
            </p>
          </div>
        </div>
      </section>

      {/* Options & Configurations */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Available Options & Configurations</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Deflection Cones</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Conical deflectors mounted at screen ends shed large debris and prevent impingement. Recommended for applications with heavy leaf/twig loading or floating debris.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Reduces large debris accumulation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Improves flow distribution</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Extends cleaning intervals</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Sealed Bearings</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Harsh-environment sealed bearing assemblies for abrasive water, heavy debris, or continuous-duty applications. Extends service life 2-3× over standard bearings.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Sand/silt protection</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Extended lubrication intervals</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Continuous-duty rated</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Mounting Kits</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Multiple mounting configurations for vertical, horizontal, or angled installations. Includes flotation collars, anchor systems, and adjustable brackets.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Floating or fixed installation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Canal, pond, or sump mounting</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Adjustable for water level changes</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Custom Configurations:</span> CTC filtration specialists can design custom SCS2 solutions for unique site conditions, extreme environments, or specialized applications. Contact us to discuss water quality, installation constraints, and regulatory requirements for your project.
            </p>
          </div>
        </div>
      </section>

      {/* Replacement Parts */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Replacement Parts & Service Components</h2>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Part Category</th>
                    <th className="px-4 py-3 text-left font-semibold">Fitment (models/sizes)</th>
                    <th className="px-4 py-3 text-left font-semibold">Part #</th>
                    <th className="px-4 py-3 text-left font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">Replacement Screens</td>
                    <td className="px-4 py-3 text-sm">Size-specific (4", 6", 8", 10", 12")</td>
                    <td className="px-4 py-3 text-sm">Contact dealer</td>
                    <td className="px-4 py-3 text-sm">3/32" or 1/8" perforations, 304 SS</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">Bearing Assemblies</td>
                    <td className="px-4 py-3 text-sm">All SCS2 models</td>
                    <td className="px-4 py-3 text-sm">Contact dealer</td>
                    <td className="px-4 py-3 text-sm">Standard or sealed bearing options</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">Seal Kits</td>
                    <td className="px-4 py-3 text-sm">All SCS2 models</td>
                    <td className="px-4 py-3 text-sm">Contact dealer</td>
                    <td className="px-4 py-3 text-sm">Complete seal replacement kit</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">Spray Bars</td>
                    <td className="px-4 py-3 text-sm">Length-specific to screen size</td>
                    <td className="px-4 py-3 text-sm">Contact dealer</td>
                    <td className="px-4 py-3 text-sm">Complete assembly with nozzles</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">Spray Nozzles</td>
                    <td className="px-4 py-3 text-sm">All SCS2 models</td>
                    <td className="px-4 py-3 text-sm">Contact dealer</td>
                    <td className="px-4 py-3 text-sm">Individual nozzles or complete set</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">Drive Motors</td>
                    <td className="px-4 py-3 text-sm">Size-specific (fractional HP)</td>
                    <td className="px-4 py-3 text-sm">Contact dealer</td>
                    <td className="px-4 py-3 text-sm">120V or 240V, NEMA rated</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">Deflection Cones</td>
                    <td className="px-4 py-3 text-sm">Size-specific (4", 6", 8", 10", 12")</td>
                    <td className="px-4 py-3 text-sm">Contact dealer</td>
                    <td className="px-4 py-3 text-sm">Optional debris deflectors</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Parts Availability:</span> All replacement parts are stocked by authorized dealers. Field service kits include bearings, seals, and hardware for annual maintenance. Screens typically have 5-10 year service life. For part numbers, availability, and pricing, contact your local dealer or CTC Waterworks engineering.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-Links Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Compare Suction Screen Options</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Link
              to="/filtration/suction-screens/standard"
              className="bg-white border-2 border-gray-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Standard Suction Screens</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                For manual-service applications with low-flow or clean water, consider Standard Suction Screens. Simple fixed design with no moving parts.
              </p>
              <div className="flex items-center text-gray-700 font-semibold">
                Compare Manual Models
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/suction-screens/psf"
              className="bg-white border-2 border-orange-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Passive Fish Screens (PSF)</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                For fish-compliant water intakes meeting NOAA/NMFS standards, consider PSF screens. No power required, passive fish protection.
              </p>
              <div className="flex items-center text-orange-600 font-semibold">
                View PSF Passive Screens
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/inline/big"
              className="bg-white border-2 border-green-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Pair with BIG Series</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Add downstream BIG inline filter for two-stage protection. SCS2 removes bulk debris, BIG provides fine polishing.
              </p>
              <div className="flex items-center text-green-600 font-semibold">
                View BIG Series Filters
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <CheckCircle
                    className={`flex-shrink-0 text-ctc-blue transition-transform ${
                      openFAQ === index ? 'transform rotate-180' : ''
                    }`}
                    size={24}
                  />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 bg-white">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Solutions Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Custom Solutions</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            SCS2 screens are configurable for multi-unit installations and specialized applications. Work with our engineering team to design the optimal intake protection solution for your unique requirements.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/SCS Bank.jpeg" alt="SCS2 Screen Bank Installation" className="w-full h-auto" />
              <div className="p-6">
                <h3 className="font-bold text-gray-900 mb-2 text-xl">Multi-Unit Installation</h3>
                <p className="text-gray-700 leading-relaxed">Multiple SCS2 screens configured in parallel banks for high-flow applications. Independent cleaning cycles ensure continuous flow while individual units are serviced.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/SCS.jpeg" alt="SCS2 Custom Application" className="w-full h-auto" />
              <div className="p-6">
                <h3 className="font-bold text-gray-900 mb-2 text-xl">Custom Application</h3>
                <p className="text-gray-700 leading-relaxed">SCS2 screens adapted for specialized installations including unique mounting configurations, custom screen materials, and site-specific environmental requirements.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-ctc-blue to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Protect Your Intake?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Our filtration specialist team will help you select the right SCS2 model, configure options, and design a complete two-stage filtration system for your water source and flow requirements.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact?topic=filtration"
              className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Talk to a Filtration Specialist
            </Link>

            <Link
              to="/filtration/size-my-filter"
              className="bg-blue-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-900 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Settings className="mr-2" size={20} />
              Size My System
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-blue-100">
              <div className="flex items-center">
                <Phone className="mr-2" size={20} />
                <span>(509) 901-7476</span>
              </div>
              <div className="flex items-center">
                <Mail className="mr-2" size={20} />
                <a href="mailto:Andre@ctcwaterworks.com" className="hover:text-white underline">
                  Andre@ctcwaterworks.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SelfCleaningSuctionScreensPage;
